import { Avatar, Box, List } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CSSProperties } from 'react';
import { AQI_LEVELS_COLORS } from 'features/aqiSensors/aqiSensors.constants';
import { LegendListItem } from './LegendListItem';
import { LegendSectionTitle } from './LegendSectionTitle';

type AQIAvatarProps = {
  color: string;
};

const circleSxProps: CSSProperties = {
  width: 16,
  height: 16,
  borderRadius: 16,
};

const AQIAvatar = (props: AQIAvatarProps): JSX.Element => {
  const { color } = props;

  return (
    <Avatar sx={{ backgroundColor: 'transparent', overflow: 'visible' }}>
      <Box sx={{ ...circleSxProps, boxShadow: `0 0 12px 8px ${color}` }}>
        <Box
          sx={{
            ...circleSxProps,
            backgroundColor: color,
            border: '2px solid white',
          }}
        />
      </Box>
    </Avatar>
  );
};

export const AQILegend = (): JSX.Element => {
  const { t } = useTranslation();

  return (
    <List
      disablePadding
      dense
      subheader={
        <LegendSectionTitle
          subtitle={t('map.legends.aqi.subtitle')}
          sx={(theme) => ({ lineHeight: theme.typography.pxToRem(16) })}
        >
          {t('map.legends.aqi.title')}
        </LegendSectionTitle>
      }
    >
      <LegendListItem
        primaryText={t('map.legends.aqi.legends.less50.title')}
        secondary={t('map.legends.aqi.legends.less50.subtitle')}
        AvatarComponent={<AQIAvatar color={AQI_LEVELS_COLORS.good} />}
      />
      <LegendListItem
        primaryText={t('map.legends.aqi.legends.less100.title')}
        secondary={t('map.legends.aqi.legends.less100.subtitle')}
        AvatarComponent={<AQIAvatar color={AQI_LEVELS_COLORS.moderate} />}
      />
      <LegendListItem
        primaryText={t('map.legends.aqi.legends.less150.title')}
        secondary={t('map.legends.aqi.legends.less150.subtitle')}
        AvatarComponent={
          <AQIAvatar color={AQI_LEVELS_COLORS.unhealthySensitive} />
        }
      />
      <LegendListItem
        primaryText={t('map.legends.aqi.legends.less200.title')}
        secondary={t('map.legends.aqi.legends.less200.subtitle')}
        AvatarComponent={<AQIAvatar color={AQI_LEVELS_COLORS.unhealthy} />}
      />
      <LegendListItem
        primaryText={t('map.legends.aqi.legends.less300.title')}
        secondary={t('map.legends.aqi.legends.less300.subtitle')}
        AvatarComponent={<AQIAvatar color={AQI_LEVELS_COLORS.veryUnhealthy} />}
      />
      <LegendListItem
        primaryText={t('map.legends.aqi.legends.more300.title')}
        secondary={t('map.legends.aqi.legends.more300.subtitle')}
        AvatarComponent={<AQIAvatar color={AQI_LEVELS_COLORS.hazardous} />}
      />
    </List>
  );
};
